import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import {
  FacilityItem,
  FacilityType,
  FacilityStatus,
  FacilityFKReferenceError,
} from "../api/facilitiesApi";

// Raw API object for a facility
interface ApiFacility {
  facilityid: string;
  hotelid: string;
  name: string;
  description: string;
  type: string;
  capacity: number;
  status: string;
  opentime: string | null;
  closetime: string | null;
  createdat: string;
  updatedat: string;
  headerphoto?: string | null;
  _media?: {
    headerphoto?: {
      key: string;
      originalValue: string;
      error?: string;
    };
  };
}

interface UseFacilitiesOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
}

export const useFacilities = ({
  hotelId,
  initialPage = 1,
  initialLimit = 20,
}: UseFacilitiesOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<FacilityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<FacilityItem | null>(null);
  const [cache, setCache] = useState<Record<string, FacilityItem>>({});
  const [searchTerm, setSearchTerm] = useState("");

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  // Helper function to transform API response to match expected structure
  const transformFacility = (facility: ApiFacility): FacilityItem => {
    const mapType = (type: string): FacilityType => {
      const outdoorTypes = ["Pool", "Garden", "Outdoor"];
      const indoorTypes = [
        "Spa",
        "Fitness",
        "Indoor",
        "Conference",
        "Business",
      ];

      if (outdoorTypes.some((t) => type.includes(t))) return "Outdoor";
      if (indoorTypes.some((t) => type.includes(t))) return "Indoor";
      return "Both";
    };

    const mapStatus = (status: string): FacilityStatus => {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === "maintenance") return "Maintenance";
      if (lowerStatus === "closed") return "Closed";
      return "Available";
    };

    // Handle S3 URL from _media field if available, otherwise fall back to headerphoto
    let headerPhoto: string | null = null;

    if (
      facility._media?.headerphoto?.key &&
      !facility._media.headerphoto.error
    ) {
      // Use the signed URL from _media field (backend provides 'signedUrl' in the response)
      const mediaHeader = facility._media.headerphoto as any;
      headerPhoto = mediaHeader.signedUrl || mediaHeader.url || mediaHeader.key;
    } else if (facility.headerphoto) {
      headerPhoto = facility.headerphoto;
    }

    return {
      facilityId: facility.facilityid,
      hotelId: facility.hotelid,
      name: facility.name,
      description: facility.description,
      type: mapType(facility.type),
      capacity: facility.capacity,
      status: mapStatus(facility.status),
      operatingHours:
        facility.opentime && facility.closetime
          ? `${facility.opentime.substring(
              0,
              5
            )}-${facility.closetime.substring(0, 5)}`
          : "",
      headerPhoto,
      createdAt: facility.createdat,
      updatedAt: facility.updatedat,
    };
  };

  // Cache management
  const updateCache = useCallback((item: FacilityItem) => {
    setCache((prev) => ({
      ...prev,
      [item.facilityId]: item,
    }));
  }, []);

  const removeFromCache = useCallback((id: string) => {
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  }, []);

  // Main facilities query (server state) backed by React Query
  const {
    data: facilitiesResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "facilities",
      hotelId,
      pagination.page,
      pagination.limit,
      searchTerm.trim() || "",
    ],
    enabled: !!hotelId,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("page", pagination.page.toString());
      params.append("limit", pagination.limit.toString());
      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const response = await callApi({
        method: "GET",
        url: `/management/hotel/facilities?${params.toString()}`,
      });

      const facilitiesData = response.data.data || response.data;

      let facilities: FacilityItem[] = [];
      let totalCount = 0;

      if (Array.isArray(facilitiesData)) {
        facilities = facilitiesData.map(transformFacility);
        totalCount = facilities.length;

        if (response.data.pagination) {
          totalCount = response.data.pagination.total;
        }
      } else if (facilitiesData && facilitiesData.data) {
        facilities = facilitiesData.data.map(transformFacility);
        if (facilitiesData.pagination) {
          totalCount = facilitiesData.pagination.total;
        }
      }

      return {
        items: facilities,
        total: totalCount,
      };
    },
  });

  // Sync query state into existing local state + pagination
  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    setError(queryError instanceof Error ? queryError : null);
  }, [queryError]);

  useEffect(() => {
    if (!facilitiesResponse) return;
    const { items: facilities, total } = facilitiesResponse;

    setItems(facilities);
    setTotal(total);
    facilities.forEach((item: FacilityItem) => updateCache(item));
  }, [facilitiesResponse, setTotal, updateCache]);

  // Backwards-compatible refetch wrapper
  const fetchItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Create item
  const createItem = useCallback(
    async (
      item: Omit<FacilityItem, "facilityId" | "createdAt" | "updatedAt">
    ) => {
      // Convert status to lowercase for API
      const apiData: any = {
        ...item,
        status: item.status.toLowerCase(),
      };

      // Map camelCase field names to snake_case for the API
      if (item.headerPhoto !== undefined) {
        apiData.headerphoto = item.headerPhoto;
        delete apiData.headerPhoto; // Remove the camelCase version
      }

      // Transform operatingHours to opentime/closetime for the API
      if (item.operatingHours !== undefined) {
        if (item.operatingHours && item.operatingHours.trim()) {
          const times = item.operatingHours.split("-");
          if (times.length === 2) {
            apiData.opentime = times[0].trim() + ":00";
            apiData.closetime = times[1].trim() + ":00";
          }
        } else {
          // If empty string, set to null to clear existing times
          apiData.opentime = null;
          apiData.closetime = null;
        }
        delete apiData.operatingHours; // Remove the frontend field
      }

      const response = await callApi({
        method: "POST",
        url: `/management/hotel/facilities`,
        data: apiData,
      });

      const createdItem = transformFacility(response.data.data);

      // Update list and cache
      setItems((prev) => [...prev, createdItem]);
      updateCache(createdItem);

      queryClient.invalidateQueries({
        queryKey: ["facilities", hotelId],
      });

      return createdItem;
    },
    [hotelId, callApi, updateCache, queryClient]
  );

  const updateItem = useCallback(
    async (id: string, updates: Partial<FacilityItem>) => {
      const originalItem = cache[id];
      if (!originalItem) throw new Error("Item not found");

      // Convert status to lowercase for API if it's being updated
      const apiData: any = { ...updates };
      if (updates.status) {
        apiData.status = updates.status.toLowerCase();
      }

      // Map camelCase field names to snake_case for the API
      if (updates.headerPhoto !== undefined) {
        apiData.headerphoto = updates.headerPhoto;
        delete apiData.headerPhoto; // Remove the camelCase version
      }

      // Transform operatingHours to opentime/closetime for the API
      if (updates.operatingHours !== undefined) {
        if (updates.operatingHours && updates.operatingHours.trim()) {
          const times = updates.operatingHours.split("-");
          if (times.length === 2) {
            apiData.opentime = times[0].trim() + ":00";
            apiData.closetime = times[1].trim() + ":00";
          }
        } else {
          // If empty string, set to null to clear existing times
          apiData.opentime = null;
          apiData.closetime = null;
        }
        delete apiData.operatingHours; // Remove the frontend field
      }

      const response = await callApi({
        method: "PUT",
        url: `/management/hotel/facilities/${id}`,
        data: apiData,
      });

      const result = transformFacility(response.data.data);

      // Update the items and cache
      setItems((prev) =>
        prev.map((item) => (item.facilityId === id ? result : item))
      );
      updateCache(result);

      queryClient.invalidateQueries({
        queryKey: ["facilities", hotelId],
      });

      return result;
    },
    [hotelId, cache, callApi, updateCache, queryClient]
  );

  // Delete item
  const deleteItem = useCallback(
    async (id: string) => {
      const itemToDelete = cache[id];
      if (!itemToDelete) throw new Error("Item not found");

      try {
        await callApi({
          method: "DELETE",
          url: `/management/hotel/facilities/${id}`,
        });

        // Update state
        setItems((prev) => prev.filter((item) => item.facilityId !== id));
        removeFromCache(id);

        queryClient.invalidateQueries({
          queryKey: ["facilities", hotelId],
        });
      } catch (error: any) {
        // Check if this is a foreign key constraint violation
        const errorMessage = error?.response?.data?.error?.message || "";
        if (
          errorMessage.includes("Invalid reference") &&
          errorMessage.includes("referenced items")
        ) {
          throw new FacilityFKReferenceError(
            "Cannot delete facility. It has active reservation requests.",
            itemToDelete.name
          );
        }
        throw error;
      }
    },
    [hotelId, cache, callApi, removeFromCache]
  );

  return {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    searchTerm,
    setSearchTerm,
    refetchItems: fetchItems,
  };
};
